import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, Download } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 90; // ~3 minutes — subscriptions can take ~2 min to confirm
const SLOW_HINT_AFTER = 10; // show reassurance after ~20s

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("checking");
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    const [booklets, setBooklets] = useState(null);
    const attemptsRef = useRef(0);
    const [slow, setSlow] = useState(false);

    useEffect(() => {
        if (!sessionId) {
            setState("failed");
            setError("Missing session id");
            return;
        }
        let alive = true;
        async function poll() {
            attemptsRef.current += 1;
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                if (!alive) return;
                setInfo(data);
                if (data.payment_status === "paid") {
                    setState("paid");
                    // Fire GA4 purchase event (once per session_id)
                    if (typeof window !== "undefined" && typeof window.gtag === "function") {
                        try {
                            window.gtag("event", "purchase", {
                                transaction_id: sessionId,
                                value: Number(data.amount) || 0,
                                currency: data.currency || "NZD",
                                items: [
                                    {
                                        item_id: data.package_label || "purchase",
                                        item_name: data.package_label || "Purchase",
                                        price: Number(data.amount) || 0,
                                        quantity: 1,
                                    },
                                ],
                            });
                        } catch (_) {
                            /* GA might be blocked — silent fail */
                        }
                    }
                    // Try to fetch booklet downloads — safe to 404 for non-booklet purchases.
                    try {
                        const dl = await api.get(`/booklets/downloads/${sessionId}`);
                        if (alive) setBooklets(dl.data.booklets || []);
                    } catch (_) {
                        /* not a booklet order — no-op */
                    }
                    return;
                }
                if (data.status === "expired") {
                    setState("expired");
                    return;
                }
                if (attemptsRef.current >= SLOW_HINT_AFTER) {
                    setSlow(true);
                }
                if (attemptsRef.current >= MAX_ATTEMPTS) {
                    setState("timeout");
                    return;
                }
                setTimeout(poll, POLL_INTERVAL);
            } catch (e) {
                if (!alive) return;
                setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                setState("failed");
            }
        }
        poll();
        return () => {
            alive = false;
        };
    }, [sessionId]);

    return (
        <div className="grid min-h-screen place-items-center bg-page px-5 py-12" data-testid="checkout-success-page">
            <div className="w-full max-w-xl border-2 border-ink/20 bg-white p-8 frame-coral lg:p-12">
                {state === "checking" && (
                    <div className="text-center" data-testid="checkout-checking">
                        <Loader2 className="mx-auto h-10 w-10 animate-spin text-coral" />
                        <div className="mt-5 font-display text-2xl font-black uppercase tracking-tight text-ink">
                            Confirming your payment…
                        </div>
                        <p className="mt-2 text-sm text-ink/65">This usually takes 5–10 seconds.</p>
                        {slow && (
                            <p className="mt-3 text-xs leading-relaxed text-ink/50" data-testid="checkout-slow-hint">
                                Stripe is still confirming — subscriptions can take up to a couple of
                                minutes. Keep this tab open; you don&apos;t need to pay again.
                            </p>
                        )}
                    </div>
                )}

                {state === "paid" && (
                    <div className="text-center" data-testid="checkout-paid">
                        <CheckCircle2 className="mx-auto h-12 w-12 text-coral" />
                        <div className="mt-5 font-display text-3xl font-black uppercase tracking-tight text-ink lg:text-4xl">
                            You&apos;re in!
                        </div>
                        <p className="mt-3 text-base leading-relaxed text-ink/75">
                            Thank you — your payment for{" "}
                            <strong className="text-ink">
                                {info?.package_label || "your package"}
                            </strong>
                            {info?.amount && info?.currency
                                ? ` (${info.currency} $${Number(info.amount).toFixed(2)})`
                                : ""}{" "}
                            went through.
                            {booklets && booklets.length > 0
                                ? " Download your booklets below — links stay live for 90 days."
                                : " We'll be in your inbox within 24 hours to kick off onboarding."}
                        </p>

                        {booklets && booklets.length > 0 && (
                            <div
                                className="mt-8 grid gap-3 text-left"
                                data-testid="booklet-downloads"
                            >
                                {booklets.map((b) => (
                                    <a
                                        key={b.slug}
                                        href={`${process.env.REACT_APP_BACKEND_URL}${b.download_url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-between gap-3 border-2 border-ink bg-white px-4 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink transition-all hover:-translate-y-0.5 hover:border-coral hover:text-coral"
                                        data-testid={`booklet-download-${b.slug}`}
                                    >
                                        <span className="truncate">{b.title}</span>
                                        <Download className="h-4 w-4 shrink-0" />
                                    </a>
                                ))}
                            </div>
                        )}

                        <div className="mt-8 flex flex-wrap justify-center gap-3">
                            <Link
                                to="/"
                                className="inline-flex items-center gap-2 border-2 border-coral bg-coral px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink hover:-translate-y-0.5"
                                data-testid="checkout-back-home"
                            >
                                <ArrowLeft className="h-4 w-4" /> Back to site
                            </Link>
                        </div>
                    </div>
                )}

                {(state === "expired" || state === "timeout") && (
                    <div className="text-center" data-testid="checkout-pending">
                        <Loader2 className="mx-auto h-10 w-10 text-ink/40" />
                        <div className="mt-5 font-display text-2xl font-black uppercase tracking-tight text-ink">
                            Still processing
                        </div>
                        <p className="mt-3 text-sm leading-relaxed text-ink/70">
                            Payment hasn&apos;t finalised yet. If you completed checkout, you&apos;ll get a
                            confirmation email shortly. You can also email{" "}
                            <strong className="text-ink">office@simpledigital.co.nz</strong> and we&apos;ll check from this end.
                        </p>
                        <Link
                            to="/"
                            className="mt-8 inline-flex items-center gap-2 border-2 border-ink/30 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink hover:bg-white hover:text-ink"
                        >
                            Back to site
                        </Link>
                    </div>
                )}

                {state === "failed" && (
                    <div className="text-center" data-testid="checkout-failed">
                        <XCircle className="mx-auto h-12 w-12 text-coral" />
                        <div className="mt-5 font-display text-2xl font-black uppercase tracking-tight text-ink">
                            Something didn&apos;t go through
                        </div>
                        <p className="mt-3 text-sm text-ink/70">
                            {error || "We couldn't confirm the payment. Please try again or get in touch."}
                        </p>
                        <Link
                            to="/#pricing"
                            className="mt-8 inline-flex items-center gap-2 border-2 border-coral bg-coral px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink hover:-translate-y-0.5"
                        >
                            Try again
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
